import { apiClient } from "@/services/apiClient";

export interface PlatformQuoteRequestListItem {
  id: string;
  title: string;
  description: string;
  status: string;
  discipline: string;
  triageResult: {
    discipline: string;
    confidence: number;
    suggestedCatalogTargetType: string | null;
    rationale: string;
  } | null;
  /**
   * Disciplines this request actually has legs for. `discipline` above reads
   * "undecided" on a two-discipline request, so this is what the list renders.
   */
  legDisciplines?: Array<"notary" | "accountant">;
  /**
   * Who asked. Mapped field-by-field on the server — the endpoint does NOT
   * send the joined user row (spec 2d40b7e, control C-2). Null when the
   * requester row is gone; the column renders a dash.
   */
  requesterName: string | null;
  requesterEmail: string | null;
  createdAt: string;
}

/** Omit `status` (or pass undefined) to fetch quote_requests in every status. */
export function listPlatformQuoteRequests(
  opts: { status?: string; limit?: number; offset?: number } = {},
): Promise<{ items: PlatformQuoteRequestListItem[]; total: number }> {
  const { status, limit, offset } = opts;
  return apiClient
    .get<{ items: PlatformQuoteRequestListItem[]; total: number }>("/platform/quote-requests", {
      // The endpoint has always returned `total` alongside `items` and taken
      // limit/offset; the page just never sent them, so an admin saw the
      // backend's default page and no way past it.
      params: {
        ...(status ? { status } : {}),
        ...(limit != null ? { limit } : {}),
        ...(offset != null ? { offset } : {}),
      },
    })
    .then((res) => res.data);
}

/**
 * `alsoNeeds` opens a SECOND leg — a second professional, quoted and paid
 * separately. It is the human-reviewed path to a multi-discipline request:
 * the model's own version of this is gated behind a flag, an admin looking at
 * the transcript is not.
 */
export function overridePlatformQuoteRequestTriage(
  id: string,
  discipline: "notary" | "accountant",
  alsoNeeds?: "notary" | "accountant" | null,
): Promise<PlatformQuoteRequestListItem> {
  return apiClient
    .post<PlatformQuoteRequestListItem>(`/platform/quote-requests/${id}/route`, {
      discipline,
      ...(alsoNeeds ? { alsoNeeds } : {}),
    })
    .then((res) => res.data);
}

export interface PlatformQuoteRequestEvent {
  id: string;
  eventType: string;
  actorUserId: string | null;
  actorDisplayName: string | null;
  actorRole: string | null;
  message: string | null;
  createdAt: string;
}

export interface PlatformQuoteRequestLeg {
  id: string;
  discipline: "notary" | "accountant";
  status: string;
  broadcastAt: string | null;
  quotingExpiresAt: string | null;
  awardedQuoteId: string | null;
  acceptedProfessionalUserId: string | null;
  certificateCheckoutId: string | null;
  childRequestId: string | null;
  childRequestType: string | null;
}

/** The intake conversation behind the request (spec add326e0 §4d.2). */
export interface PlatformQuoteIntake {
  conversationId: string;
  status: string;
  model: string | null;
  messages: Array<{ role: string; content: string; at: string }>;
  draft: {
    title?: string;
    summary?: string;
    discipline?: "notary" | "accountant" | null;
    alsoNeeds?: "notary" | "accountant" | null;
    confidence?: number;
    rationale?: string;
  } | null;
}

export interface PlatformQuote {
  id: string;
  legId: string;
  legDiscipline: "notary" | "accountant" | null;
  professionalUserId: string;
  professionalDisplayName: string | null;
  priceMinor: number;
  currency: string;
  estimatedDays: number | null;
  message: string | null;
  status: string;
  createdAt: string;
}

export interface PlatformQuoteClarification {
  id: string;
  legId: string;
  legDiscipline: "notary" | "accountant" | null;
  askedByUserId: string;
  askedByName: string | null;
  /** The same "Profesional N" number the quotes list and the timeline use. */
  askedByOrdinal: number | null;
  question: string;
  answer: string | null;
  answeredAt: string | null;
  createdAt: string;
  /** Non-null means the question never reached the consumer. */
  blockedReason: "contact_details" | "ai_flagged" | null;
}

export interface PlatformQuoteRequestDetail {
  id: string;
  requestedByUserId: string;
  requesterName: string | null;
  requesterEmail: string | null;
  title: string;
  description: string;
  status: string;
  discipline: string;
  triageStatus: string;
  triageResult: PlatformQuoteRequestListItem["triageResult"];
  triageModel: string | null;
  childRequestId: string | null;
  childRequestType: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Where the job goes, SNAPSHOTTED at the moment the consumer chose it — not
   * a join to their current address book (spec 218021de, C-3). All null on
   * requests created before delivery addresses were collected.
   */
  deliveryAddressId: string | null;
  deliveryFormattedAddress: string | null;
  deliveryAdminArea: string | null;
  deliveryCity: string | null;
  deliveryCountryCode: string | null;
  deliveryContactPhone: string | null;
  deliveryCourierNotes: string | null;
  /**
   * Every question a professional asked about this request, INCLUDING the ones
   * moderation stopped — those are invisible on every other surface, so admin
   * is the only audience that sees them.
   *
   * The asking professional IS named here, admin-only. The pro and consumer
   * payloads still carry no asker identity (spec b3430a37, control C-2).
   */
  clarifications: PlatformQuoteClarification[];
  legs: PlatformQuoteRequestLeg[];
  intake: PlatformQuoteIntake | null;
  events: PlatformQuoteRequestEvent[];
  quotes: PlatformQuote[];
}

/**
 * THE ARRAYS ARE DEFAULTED BECAUSE THE PORTAL AND THE API DO NOT SHIP
 * TOGETHER, and the gap is minutes long every single release.
 *
 * Vercel has the new portal live within seconds of a merge; the Railway API
 * takes 5-13 minutes to build the monorepo and cycle its container. For that
 * whole window the NEW portal is talking to the OLD backend. Any field added
 * to this DTO in the same release is therefore `undefined` in production for
 * minutes, on every deploy, by construction.
 *
 * That is not hypothetical: `clarifications` shipped on 2026-10-12 and
 * `data.clarifications.length` took the admin RFQ detail page to the error
 * boundary — a blank "Algo salió mal" over a page whose other eight cards were
 * fine — until the API caught up.
 *
 * The TYPE says these are always arrays, which is true of the contract and
 * false of the wire during a rollout. Defaulting here, at the boundary where
 * the untrusted payload becomes a typed value, keeps that lie from reaching
 * nine render sites that each have to remember it. A missing list then renders
 * as an empty list — which is what an admin should see for a request whose
 * backend cannot describe it yet, rather than nothing at all.
 */
export function getPlatformQuoteRequestDetail(id: string): Promise<PlatformQuoteRequestDetail> {
  return apiClient
    .get<PlatformQuoteRequestDetail>(`/platform/quote-requests/${id}`)
    .then((res) => ({
      ...res.data,
      clarifications: res.data?.clarifications ?? [],
      legs: res.data?.legs ?? [],
      events: res.data?.events ?? [],
      quotes: res.data?.quotes ?? [],
    }));
}
